import { FC, useCallback, useEffect, useState } from 'react';
import { ory } from '@/auth';
import { createFileRoute } from '@tanstack/react-router';
import type { LinkProps } from '@tanstack/react-router';
import { zodSearchValidator } from '@tanstack/router-zod-adapter';
import { z } from 'zod';
import Button from '@/components/core/Button.tsx';
import { FormattedMessage, useIntl } from 'react-intl';
import { IconChevronRight, IconExternalLink } from '@/icons';
import { useBranding } from '@/hooks/useBranding';
import { Section } from '@/components/Section';
import { useTenantID } from '@/hooks/useTenantID';
import { cn } from '@/helpers/classnames';
import { useConfig } from '@/contexts/ConfigContext';
import { DocumentHead } from '@/components/core/DocumentHead';
import { ErrorBanner } from '@/components/core/ErrorBanner';
import { useFavicon } from '@/hooks/useFavicon';
import { INACTIVITY_LOGOUT_KEY } from '@/hooks/useInactivityTimeout';
import { toast } from '@/hooks/useToast';
import {
  buildBrowserLoginFlowRequest,
  getOryErrorId,
  getSafeLoginReturnTo,
  isExistingOrySession,
} from '@/helpers/loginFlow';

const loginSearchSchema = z.object({
  returnTo: z.string().optional(),
});

export const Route = createFileRoute('/$tenantId/login')({
  component: Login,
  validateSearch: zodSearchValidator(loginSearchSchema),
});

interface LargeButtonProps {
  onClick?: () => void;
  to?: LinkProps['to'];
  title?: string;
  subtitle?: string;
  isLoading?: boolean;
}

const LargeButton: FC<LargeButtonProps> = ({
  onClick,
  to,
  title,
  subtitle,
  isLoading = false,
}) => {
  return (
    <Button
      size="xl"
      className="w-full py-5 text-left hover:bg-primary-400 hover:text-contrast-primary"
      loading={isLoading}
      onClick={onClick}
      to={to}
    >
      <div className="flex w-full flex-row items-center justify-center gap-2">
        <div className="flex w-full flex-col items-start gap-2">
          <span className="leading-5">{title}</span>
          {subtitle && (
            <span className="text-base leading-4 opacity-90">{subtitle}</span>
          )}
        </div>
        <IconChevronRight className="size-8" />
      </div>
    </Button>
  );
};

function Login() {
  const tenantId = useTenantID();
  const { returnTo } = Route.useSearch();
  const [error, setError] = useState<string | null>(null);
  const [showSpinner, setShowSpinner] = useState<boolean>(false);
  const { formatMessage } = useIntl();
  const branding = useBranding();

  useFavicon(tenantId);

  useEffect(() => {
    if (sessionStorage.getItem('inactivity_logout_toast_shown')) {
      return;
    }
    if (localStorage.getItem(INACTIVITY_LOGOUT_KEY)) {
      sessionStorage.setItem('inactivity_logout_toast_shown', '1');
      toast.warning({
        title: formatMessage({
          defaultMessage: 'Du har blivit utloggad på grund av inaktivitet',
        }),
      });
    }
  }, [formatMessage]);

  const onClickLogin = useCallback(async () => {
    setShowSpinner(true);
    setError(null);
    const returnUrl = getSafeLoginReturnTo(
      returnTo,
      window.location.origin,
      tenantId,
    );

    // Check whether the Kratos session is still alive. The inactivity
    // hook deliberately preserves it across an inactivity logout so the
    // user does not have to redo MFA on re-entry — we still want to
    // re-verify the password, but not the second factor.
    let session: Awaited<ReturnType<typeof ory.toSession>>['data'] | undefined;
    let whoamiError: unknown;
    try {
      ({ data: session } = await ory.toSession());
    } catch (err) {
      whoamiError = err;
    }

    const loginRequest = buildBrowserLoginFlowRequest({
      returnTo: returnUrl,
      organization: tenantId,
      sessionExists: isExistingOrySession(session, whoamiError),
    });

    try {
      const { data } = await ory
        .createBrowserLoginFlow(loginRequest)
        .catch((err) => {
          // Disallowed returnTo is reported as security_identity_mismatch.
          if (getOryErrorId(err) !== 'security_identity_mismatch') {
            throw err;
          }
          return ory.createBrowserLoginFlow({
            ...loginRequest,
            returnTo: `${window.location.origin}/${tenantId}`,
          });
        });
      localStorage.removeItem(INACTIVITY_LOGOUT_KEY);
      sessionStorage.removeItem('inactivity_logout_toast_shown');
      window.location.href = data.request_url;
    } catch (err) {
      console.error(err, getOryErrorId(err));
      setError(
        formatMessage({
          defaultMessage: 'Inloggningen kunde inte startas. Försök igen.',
        }),
      );
    } finally {
      setShowSpinner(false);
    }
  }, [returnTo, tenantId, formatMessage]);

  const { disabled } = useConfig();

  if (disabled) {
    return null;
  }

  return (
    <>
      <DocumentHead
        titleKey="page.login.title"
        descriptionKey="page.login.description"
      />
      <a
        href="#main-content"
        className="sr-only focus:not-sr-only focus:absolute focus:z-50 focus:rounded focus:bg-background-50 focus:p-4 focus:text-text-950 focus:shadow-lg"
      >
        <FormattedMessage defaultMessage="Hoppa till huvudinnehåll" />
      </a>
      <main
        id="main-content"
        className={cn('flex h-screen w-full p-5', tenantId)}
      >
        <Section className="m-auto size-fit flex-col overflow-hidden !p-0 md:flex-row">
          <div className="flex max-w-panel-full grow items-center justify-center rounded-lg">
            <img
              src={branding?.welcome}
              alt=""
              role="presentation"
              className="size-full object-cover"
            />
          </div>
          <div className="m-auto flex w-full max-w-lg flex-col items-center gap-3 p-8 md:w-[400px] md:min-w-[400px]">
            <div className="h-[100px] w-full overflow-hidden">
              {branding?.logo}
            </div>
            <h1 className="mb-8 text-center text-3xl font-medium text-text-950">
              <FormattedMessage
                defaultMessage="Välkommen till {serviceName}!"
                values={{ serviceName: branding?.serviceName }}
              />
            </h1>
            {error && <ErrorBanner className="w-full">{error}</ErrorBanner>}
            <LargeButton
              title={formatMessage({
                defaultMessage: 'Logga in som administratör',
              })}
              isLoading={showSpinner}
              onClick={onClickLogin}
            />
            <LargeButton
              title={formatMessage({
                defaultMessage: 'Ansök om företagskonto',
              })}
              to="/$tenantId/register"
            />
            <LargeButton
              title={formatMessage({ defaultMessage: 'Självbetjäning' })}
              subtitle={formatMessage({
                defaultMessage: 'För resenärer, elever och vårdnadshavare',
              })}
              to="/$tenantId/self-service/login"
            />
            <a
              href="/accessibility"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Tillgänglighetsredogörelse (öppnas i ny flik)"
              className="mx-auto mt-10 inline-flex items-center justify-center rounded-md px-4 py-2 text-sm font-medium text-text-950 hover:bg-background-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-primary-600"
            >
              <FormattedMessage defaultMessage="Tillgänglighetsredogörelse" />
              <IconExternalLink className="ml-2 size-4" aria-hidden="true" />
            </a>
          </div>
        </Section>
      </main>
    </>
  );
}
